import { useCallback, useEffect, useState } from "react";
import { workerService } from "@safers/api";
import { requestFcmToken } from "@safers/pwa";

interface UsePushSubscriptionReturn {
  subscribed: boolean;
  busy: boolean;
  status: string;
  /**
   * 구독 시도. 성공 여부를 반환한다.
   *
   * 반환값은 온보딩의 pushProbe 로도 쓰인다 — 권한을 허용했는데도 false 라면
   * 토큰 발급 자체가 불가능한 환경(Play 서비스 미탑재 등)이라는 뜻이다. (#176)
   */
  subscribe: () => Promise<boolean>;
}

export function usePushSubscription(uuid: string): UsePushSubscriptionReturn {
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      if (Notification.permission !== "granted") return;
      const reg = await navigator.serviceWorker.ready;
      try {
        const token = await requestFcmToken(reg);
        if (token && !cancelled) {
          await workerService.registerWorkerFcmToken(uuid, token);
          setSubscribed(true);
        }
      } catch {
        return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  const subscribe = useCallback(async () => {
    setBusy(true);
    setStatus("");
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("이 브라우저는 알림을 지원하지 않습니다.");
      }
      if (Notification.permission !== "granted") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          setStatus("알림 권한이 거부되었습니다. 브라우저 설정에서 알림을 허용해 주세요.");
          return false;
        }
      }
      const reg = await navigator.serviceWorker.ready;
      const token = await requestFcmToken(reg);
      if (!token) throw new Error("FCM 토큰을 발급하지 못했습니다.");
      await workerService.registerWorkerFcmToken(uuid, token);
      setSubscribed(true);
      setStatus("알림이 설정되었습니다.");
      return true;
    } catch (e) {
      if (e instanceof Error && e.name === "NotAllowedError") {
        setStatus("알림 권한이 거부되었습니다. 브라우저 설정에서 알림을 허용해 주세요.");
      } else {
        setStatus(e instanceof Error ? e.message : String(e));
      }
      return false;
    } finally {
      setBusy(false);
    }
  }, [uuid]);

  return { subscribed, busy, status, subscribe };
}
